/**
 * Onboarding wizard — the first-login, full-screen bento flow.
 *
 * ⚠️ THIS DOMAIN DOES NOT OWN THE ONBOARDING RECORD. The conversation does.
 *
 * The wizard and Tessy are two VIEWS of one thing. Everything the user
 * accomplishes here is written into the assistant conversation as messages with
 * blocks (`channel-picker`, `import-progress`), which is why closing the wizard
 * loses nothing: the transcript already holds the channel that was connected and
 * the import that is running, and the floating panel renders the same blocks.
 *
 * What lives here is only what the wizard needs to DRIVE itself — which screen
 * is showing, which connector is selected, whether we are still waiting on an
 * OAuth round-trip. None of it is worth persisting; all of it is reconstructible
 * from the conversation plus the integrations readmodel.
 */

export type OnboardingWizardStep = 'channel' | 'nextSteps';

/**
 * Why a connection attempt ended without a channel appearing. `timedOut` is a
 * real outcome, not an error: OAuth happens in ANOTHER TAB, so this tab cannot
 * tell "still authorizing" from "closed the tab and walked away". After the
 * bound we stop waiting and offer to re-check rather than spinning forever.
 */
export type ConnectionWait = 'idle' | 'waiting' | 'timedOut';

export type OnboardingWizardState = {
  /** Whether the full-screen wizard is mounted. */
  open: boolean;
  step: OnboardingWizardStep;

  /** Connector highlighted in the bento, before the user commits. */
  selectedConnectorId: string | null;

  /** Store URL / credentials the setup panel collected, for connectors that need them.
   *  ⚠️ Local to the wizard ON PURPOSE — never dispatched into the conversation,
   *  which is persisted server-side as an aggregate. See `connectPanel`. */
  connectPanelOpen: boolean;

  connectionWait: ConnectionWait;

  /** True once the integrations subscription has delivered a snapshot. */
  integrationsSnapshotReady: boolean;

  /**
   * How the connected channel got there. `preexisting` means it was already
   * connected when the wizard opened (the Mercado Libre sign-up path), which is
   * what tells the policy NOT to start a second import.
   */
  connectionOrigin: 'wizard' | 'preexisting' | null;

  /** The integration that appeared after a successful connect. */
  connectedIntegrationId: string | null;
  connectedConnectorId: string | null;
  /**
   * The other half of the integration id.
   *
   * Carried explicitly rather than derived by splitting `connectedIntegrationId`
   * because the batch-process START commands take the PAIR — `{ connectorId,
   * integrationUserId }` — never the joined id, and the readmodel row the wizard
   * detects already carries both fields. Re-deriving one from the other is how
   * the separator bug got in; see `integrationId.ts`.
   */
  connectedIntegrationUserId: string | null;
  connectedLabel: string | null;

  /** Auto-import of the channel's existing PUBLICATIONS, fired once it appeared. */
  importBatchProcessId: string | null;
  /**
   * True once we know no publication run could be attached — the start was
   * rejected, or a resumed wizard found none in flight. Distinct from "not yet":
   * without it the screen cannot tell "still starting" from "never started", and
   * a card that says neither is the reported symptom.
   */
  importUnavailable: boolean;

  /**
   * Historical ORDER seeding — a SEPARATE batch process with its own id.
   *
   * Deliberately not folded into `importBatchProcessId`: the two runs are
   * independent, either can fail alone, and one id cannot represent two of them.
   */
  orderImportBatchProcessId: string | null;
  /** The user explicitly asked for the order history. Never set on its own. */
  orderImportRequested: boolean;
  /** The requested order seeding could not be started. */
  orderImportUnavailable: boolean;

  /** Set when the connect call itself failed, so the bento can say so in place. */
  error: string | null;
};

export const initialState: OnboardingWizardState = {
  open: false,
  step: 'channel',
  selectedConnectorId: null,
  connectPanelOpen: false,
  connectionWait: 'idle',
  integrationsSnapshotReady: false,
  connectionOrigin: null,
  connectedIntegrationId: null,
  connectedConnectorId: null,
  connectedIntegrationUserId: null,
  connectedLabel: null,
  importBatchProcessId: null,
  importUnavailable: false,
  orderImportBatchProcessId: null,
  orderImportRequested: false,
  orderImportUnavailable: false,
  error: null
};

export const selectors = {
  isOpen: ( state: OnboardingWizardState ) => state.open,
  step: ( state: OnboardingWizardState ) => state.step,
  selectedConnectorId: ( state: OnboardingWizardState ) => state.selectedConnectorId,
  connectPanelOpen: ( state: OnboardingWizardState ) => state.connectPanelOpen,
  connectionWait: ( state: OnboardingWizardState ) => state.connectionWait,
  isWaiting: ( state: OnboardingWizardState ) => state.connectionWait === 'waiting',
  hasTimedOut: ( state: OnboardingWizardState ) => state.connectionWait === 'timedOut',
  integrationsSnapshotReady: ( state: OnboardingWizardState ) => state.integrationsSnapshotReady,
  connectionOrigin: ( state: OnboardingWizardState ) => state.connectionOrigin,
  connectedIntegrationId: ( state: OnboardingWizardState ) => state.connectedIntegrationId,
  connectedConnectorId: ( state: OnboardingWizardState ) => state.connectedConnectorId,
  connectedIntegrationUserId: ( state: OnboardingWizardState ) => state.connectedIntegrationUserId,
  connectedLabel: ( state: OnboardingWizardState ) => state.connectedLabel,
  importBatchProcessId: ( state: OnboardingWizardState ) => state.importBatchProcessId,
  importUnavailable: ( state: OnboardingWizardState ) => state.importUnavailable,
  /** Own selector, own field — the orders run is not a variant of the publications one. */
  orderImportBatchProcessId: ( state: OnboardingWizardState ) => state.orderImportBatchProcessId,
  orderImportRequested: ( state: OnboardingWizardState ) => state.orderImportRequested,
  orderImportUnavailable: ( state: OnboardingWizardState ) => state.orderImportUnavailable,
  error: ( state: OnboardingWizardState ) => state.error,

  /**
   * The wizard may advance only once a channel actually exists. Selecting a card
   * is not progress — a user who picked Shopify and never authorized has
   * connected nothing, and screen 2 talks about an import that would not exist.
   */
  canAdvance: ( state: OnboardingWizardState ) =>
    state.connectedIntegrationId !== null
};
